import React, { useMemo } from 'react';
import { Box, Button, InputAdornment, MenuItem, Paper, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { AuditFilterOptions, AuditFilters } from './auditApi';
import { isDefaultFilters, isRangeValid, lastDaysPeriod } from './auditFormat';
import { getTodayStr } from '../reports/edit/editCalc';

const BORDER = '1px solid #e2e8f0';
const PRESET_SX = { textTransform: 'none', fontWeight: 600, color: '#475569' } as const;

interface Props {
  filters: AuditFilters;
  options: AuditFilterOptions | null;
  onChange: (patch: Partial<AuditFilters>) => void;
  onReset: () => void;
}

/** Фильтры журнала: период, модуль, действие, вид, результат, пользователь, аптека и поиск по тексту */
const AuditFiltersBar: React.FC<Props> = ({ filters, options, onChange, onReset }) => {
  const today = getTodayStr();
  const rangeOk = isRangeValid(filters.from, filters.to);

  const actions = useMemo(() => {
    if (!options) return [];
    return filters.module ? options.actions.filter((item) => item.module === filters.module) : options.actions;
  }, [options, filters.module]);

  const preset = (days: number) => onChange(lastDaysPeriod(days));
  const selectProps = { select: true, size: 'small' as const, SelectProps: { displayEmpty: true } };

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: BORDER, borderRadius: '12px' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Поиск по действию, объекту, пользователю, аптеке"
          value={filters.q}
          onChange={(e) => onChange({ q: e.target.value })}
          inputProps={{ maxLength: 100 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            )
          }}
          sx={{ flex: '1 1 320px', minWidth: 260 }}
        />
        <TextField
          size="small"
          label="С даты"
          type="date"
          value={filters.from}
          onChange={(e) => onChange({ from: e.target.value })}
          error={!rangeOk}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: today }}
          sx={{ width: 165 }}
        />
        <TextField
          size="small"
          label="По дату"
          type="date"
          value={filters.to}
          onChange={(e) => onChange({ to: e.target.value })}
          error={!rangeOk}
          helperText={rangeOk ? undefined : 'Начало позже конца'}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: today }}
          sx={{ width: 165 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button size="small" onClick={() => preset(1)} sx={PRESET_SX}>Сегодня</Button>
          <Button size="small" onClick={() => preset(7)} sx={PRESET_SX}>7 дней</Button>
          <Button size="small" onClick={() => preset(30)} sx={PRESET_SX}>30 дней</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 2 }}>
        <TextField
          {...selectProps}
          value={filters.module}
          onChange={(e) => onChange({ module: e.target.value })}
          sx={{ width: 190 }}
        >
          <MenuItem value="">Все модули</MenuItem>
          {options && options.modules.map((item) => <MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>)}
        </TextField>
        <TextField
          {...selectProps}
          value={filters.action}
          onChange={(e) => onChange({ action: e.target.value })}
          sx={{ width: 240 }}
        >
          <MenuItem value="">Все действия</MenuItem>
          {actions.map((item) => <MenuItem key={item.code} value={item.code}>{item.title}</MenuItem>)}
        </TextField>
        <TextField
          {...selectProps}
          value={filters.kind}
          onChange={(e) => onChange({ kind: e.target.value })}
          sx={{ width: 190 }}
        >
          <MenuItem value="">Любой вид</MenuItem>
          {options && options.kinds.map((item) => <MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>)}
        </TextField>
        <TextField
          {...selectProps}
          value={filters.result}
          onChange={(e) => onChange({ result: e.target.value })}
          sx={{ width: 160 }}
        >
          <MenuItem value="">Любой результат</MenuItem>
          {options && options.results.map((item) => <MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>)}
        </TextField>
        <TextField
          size="small"
          label="Пользователь"
          placeholder="Логин или ФИО"
          value={filters.user}
          onChange={(e) => onChange({ user: e.target.value })}
          inputProps={{ maxLength: 100 }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 190 }}
        />
        <TextField
          size="small"
          label="Аптека"
          placeholder="Номер или логин"
          value={filters.pharmacy}
          onChange={(e) => onChange({ pharmacy: e.target.value })}
          inputProps={{ maxLength: 30 }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <Button size="small" onClick={onReset} disabled={isDefaultFilters(filters)} sx={PRESET_SX}>
          Сбросить
        </Button>
      </Box>
    </Paper>
  );
};

export default React.memo(AuditFiltersBar);
